"use client";

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useParams } from "next/navigation";
import type { PlaidLinkOnExitMetadata } from "react-plaid-link";
import type { DeviceType } from "utils/src/uaDevicesParse/ua-device-parser";
import log from "logbeacon";

import { PlaidLinkWebSession } from "./PlaidLinkWebSession";
import { isPlaidUserCanceledError } from "./plaidFlowErrors";
import { isPlaidOAuthRedirect } from "./plaidOAuth";
import {
  type FetchLinkTokenParams,
  useCreateLinkToken,
} from "./useCreateLinkToken";
import type { PlaidMemberAuthorizationSuccessOutcome } from "./memberPlaidApi";
import {
  type PlaidLinkExitError,
  useExchangeAccessToken,
} from "./useExchangeAccessToken";
import { isPlaidNativeLinkRoute } from "./plaidPlatform";
import { openPlaidLinkInApp } from "@/src/lib/bridge/plaidLink";
import { useMemoizedFn } from "ahooks";

export interface OpenPlaidLinkParams {
  /** Plaid update mode — existing connection internal ID */
  connectionId?: string;
}

export interface UsePlaidLinkFlowOptions {
  autoInit?: boolean;
  onBindSuccess?: (
    outcome: PlaidMemberAuthorizationSuccessOutcome,
  ) => void | Promise<void>;
  onBindError?: (error: unknown) => void | Promise<void>;
  /** User left Plaid Link without bind (web onExit null/user-cancel, native cancel). */
  onLinkExit?: () => void | Promise<void>;
  /** Authorization returned INSTITUTION_ALREADY_LINKED (146114) with update link_token. */
  onInstitutionAlreadyLinked?: (
    updateLinkToken: string,
  ) => void | Promise<void>;
}

export interface WebPlaidSessionState {
  linkToken: string;
  sessionKey: number;
  autoOpen: boolean;
  receivedRedirectUri?: string;
}

export interface UsePlaidLinkFlowResult {
  /** 按钮转圈：仅 link token 拉取 + 打开 Plaid（不含 bind） */
  loading: boolean;
  /** 绑定中：禁用按钮但不显示转圈，避免整页「假死」观感 */
  binding: boolean;
  linkToken: string | null;
  tokenError: string | null;
  openLink: (params?: OpenPlaidLinkParams) => Promise<void>;
  /** Open Plaid with an existing link_token (e.g. 146114 update token). Skips createLinkToken. */
  openLinkWithToken: (linkToken: string) => Promise<void>;
  /** Web/desktop: render once in the consumer tree so PlaidLinkWebSession can mount. */
  webLinkHost: ReactNode;
}

const toLinkExitError = (
  error: {
    error_type?: string;
    error_code?: string;
    error_message?: string;
    display_message?: string;
  } | null,
  metadata?: PlaidLinkOnExitMetadata,
): PlaidLinkExitError => ({
  error_type: error?.error_type ?? "",
  error_code: error?.error_code ?? "",
  error_message: error?.error_message ?? "",
  display_message: error?.display_message ?? "",
  institution_name: metadata?.institution?.name ?? "",
});

export const usePlaidLinkFlow = (
  options: UsePlaidLinkFlowOptions = {},
): UsePlaidLinkFlowResult => {
  const {
    autoInit = false,
    onBindSuccess,
    onBindError,
    onLinkExit,
    onInstitutionAlreadyLinked,
  } = options;
  const { device } = useParams<{ device?: DeviceType }>();
  const isInApp = isPlaidNativeLinkRoute(device);
  const [opening, setOpening] = useState(false);
  const [webSession, setWebSession] = useState<WebPlaidSessionState | null>(
    null,
  );
  const sessionKeyRef = useRef(0);
  const openGenerationRef = useRef(0);
  const activeLinkTokenRef = useRef<string | null>(null);
  const oauthSessionStartedRef = useRef(false);

  const {
    linkToken,
    loading: tokenLoading,
    error: tokenErrorState,
    createLinkToken,
  } = useCreateLinkToken({ autoInit });

  const {
    finalizePlaidAuthorization,
    reportLinkExitError,
    loading: authorizationLoading,
    error: authorizationError,
  } = useExchangeAccessToken();

  const clearWebSession = useMemoizedFn(() => {
    setWebSession(null);
  });

  const completeLinkSession = useMemoizedFn(async () => {
    try {
      const activeLinkToken = activeLinkTokenRef.current ?? linkToken;
      if (!activeLinkToken) {
        throw new Error("Missing link token for authorization");
      }
      const outcome = await finalizePlaidAuthorization(activeLinkToken);
      if (!outcome) {
        throw new Error(authorizationError ?? "Plaid authorization failed");
      }

      if (outcome.kind === "institution_already_linked") {
        await onInstitutionAlreadyLinked?.(outcome.updateLinkToken);
        return;
      }

      if (!outcome.connectionId) {
        throw new Error(authorizationError ?? "Plaid authorization failed");
      }

      await onBindSuccess?.(outcome);
    } catch (error) {
      log.error("[usePlaidLinkFlow][completeLinkSession] bind failed", error);
      await onBindError?.(error);
    } finally {
      clearWebSession();
      setOpening(false);
    }
  });

  const onPlaidLinkCompleted = useMemoizedFn(async () => {
    await completeLinkSession();
  });

  const onPlaidExit = useMemoizedFn(
    async (
      error: {
        error_type?: string;
        error_code?: string;
        error_message?: string;
        display_message?: string;
      } | null,
      metadata: PlaidLinkOnExitMetadata,
    ) => {
      setOpening(false);
      clearWebSession();
      if (error == null || isPlaidUserCanceledError(error)) {
        await onLinkExit?.();
        return;
      }
      await reportLinkExitError(toLinkExitError(error, metadata));
      log.error("[usePlaidLinkFlow][onPlaidExit] bind failed", error, metadata);

      await onBindError?.(error);
    },
  );

  const mountWebSession = useMemoizedFn(
    (token: string, autoOpen: boolean, receivedRedirectUri?: string): void => {
      activeLinkTokenRef.current = token;
      sessionKeyRef.current += 1;
      setWebSession({
        linkToken: token,
        sessionKey: sessionKeyRef.current,
        autoOpen,
        receivedRedirectUri,
      });
    },
  );

  useEffect(() => {
    if (isInApp || !linkToken || oauthSessionStartedRef.current) {
      return;
    }
    if (!isPlaidOAuthRedirect()) {
      return;
    }
    oauthSessionStartedRef.current = true;
    setOpening(true);
    mountWebSession(
      linkToken,
      true,
      typeof window !== "undefined" ? window.location.href : undefined,
    );
  }, [isInApp, linkToken, mountWebSession]);

  const openLinkWithToken = useMemoizedFn(async (token: string) => {
    setOpening(true);
    const generation = ++openGenerationRef.current;

    try {
      if (!isInApp) {
        clearWebSession();
      }

      if (generation !== openGenerationRef.current) {
        return;
      }

      if (isInApp) {
        try {
          activeLinkTokenRef.current = token;
          await openPlaidLinkInApp({ linkToken: token });
          await completeLinkSession();
        } catch (error) {
          if (isPlaidUserCanceledError(error)) {
            await onLinkExit?.();
            return;
          }
          log.error("[usePlaidLinkFlow][onPlaidExit] bind failed", error);
          await onBindError?.(error);
        } finally {
          setOpening(false);
        }
        return;
      }

      mountWebSession(token, true);
    } catch (error) {
      if (generation !== openGenerationRef.current) {
        return;
      }
      if (isPlaidUserCanceledError(error)) {
        setOpening(false);
        return;
      }
      log.error("[usePlaidLinkFlow][openLinkWithToken] bind failed", error);
      await onBindError?.(error);
      setOpening(false);
    }
  });

  const openLink = useMemoizedFn(async (params?: OpenPlaidLinkParams) => {
    setOpening(true);
    const generation = ++openGenerationRef.current;
    const fetchParams: FetchLinkTokenParams | undefined = params?.connectionId
      ? { connectionId: params.connectionId }
      : undefined;

    try {
      if (!isInApp) {
        clearWebSession();
      }

      const token = await createLinkToken(fetchParams);
      if (generation !== openGenerationRef.current) {
        return;
      }

      if (!token) {
        throw new Error(
          tokenErrorState?.error_message ?? "Failed to create link token",
        );
      }

      if (isInApp) {
        try {
          activeLinkTokenRef.current = token;
          await openPlaidLinkInApp({ linkToken: token });
          await completeLinkSession();
        } catch (error) {
          if (isPlaidUserCanceledError(error)) {
            await onLinkExit?.();
            return;
          }
          log.error("[usePlaidLinkFlow][openLink] bind failed", error);
          await onBindError?.(error);
        } finally {
          setOpening(false);
        }
        return;
      }

      mountWebSession(token, true);
    } catch (error) {
      if (generation !== openGenerationRef.current) {
        return;
      }
      if (isPlaidUserCanceledError(error)) {
        setOpening(false);
        return;
      }
      log.error("[usePlaidLinkFlow][openLink] bind failed", error);
      await onBindError?.(error);
      setOpening(false);
    }
  });

  const handleWebOpened = useMemoizedFn(() => {
    setOpening(false);
  });

  const webLinkHost = useMemo(() => {
    if (isInApp || !webSession) {
      return null;
    }
    return (
      <PlaidLinkWebSession
        key={`${webSession.linkToken}-${webSession.sessionKey}`}
        linkToken={webSession.linkToken}
        autoOpen={webSession.autoOpen}
        receivedRedirectUri={webSession.receivedRedirectUri}
        onLinkCompleted={onPlaidLinkCompleted}
        onExit={onPlaidExit}
        onOpened={handleWebOpened}
      />
    );
  }, [handleWebOpened, isInApp, onPlaidExit, onPlaidLinkCompleted, webSession]);

  const tokenError =
    tokenErrorState?.error_message ?? authorizationError ?? null;
  const openingLoading = tokenLoading || opening;
  const binding = authorizationLoading;

  return {
    loading: openingLoading,
    binding,
    linkToken,
    tokenError,
    openLink,
    openLinkWithToken,
    webLinkHost,
  };
};
